/**
 * Resolves the engine's configuration from the `<script>` tag that loaded it, so a
 * customer configures the engine through snippet attributes rather than a separate
 * config call.
 *
 * `getLoaderScript` takes the **last** matching tag, because a page that includes
 * the snippet twice should use the most recent one rather than the first.
 */
(function (window, document) {
  function readBoolean(value) {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const lower = value.toLowerCase();
      return lower === "true" || lower === "1";
    }
    return false;
  }

  function getLoaderScript() {
    const scripts = document.querySelectorAll('script[src*="engine.js"],script[data-mvt="engine"]');
    if (scripts.length === 0) return null;
    return scripts[scripts.length - 1];
  }

  const DEFAULTS = {
    projectKeyParam: "projectKey",
    assignmentCookieNamePrefix: "abtest_group",
    assignmentCookieDays: 30,
    flickerClass: "abtest-hidden",
    cookieConsentEnabled: false,

    cacheEnabled: true,
    revealFastMs: 1200,
    revealMaxMs: 2000,
    targetRevealMaxMs: 4000,

    serveEnabled: false,
  };

  function resolveFromScriptAttributes(script) {
    if (!script) return {};
    return {
      projectKeyParam: script.getAttribute("data-project-key-param"),
      assignmentCookieNamePrefix: script.getAttribute("data-cookie-prefix"),
      assignmentCookieDays: (function () {
        const v = script.getAttribute("data-cookie-days");
        return v != null ? parseInt(v, 10) : undefined;
      })(),
      flickerClass: script.getAttribute("data-flicker-class"),

      inlineProjectKey: script.getAttribute("data-project-key"),

      debug: (function () {
        const v = script.getAttribute("data-debug");
        return v == null ? undefined : readBoolean(v);
      })(),
      cookieConsentEnabled: (function () {
        const v = script.getAttribute("data-cookie-consent-enabled");
        return v == null ? undefined : readBoolean(v);
      })(),

      cacheEnabled: (function () {
        const v = script.getAttribute("data-cache-enabled");
        return v == null ? undefined : readBoolean(v);
      })(),
      revealFastMs: (function () {
        const v = script.getAttribute("data-reveal-fast-ms");
        if (v == null) return undefined;
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : undefined;
      })(),
      revealMaxMs: (function () {
        const v = script.getAttribute("data-reveal-max-ms");
        if (v == null) return undefined;
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : undefined;
      })(),
      targetRevealMaxMs: (function () {
        const v = script.getAttribute("data-target-reveal-max-ms");
        if (v == null) return undefined;
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : undefined;
      })(),
      serveEnabled: (function () {
        const v = script.getAttribute("data-serve-enabled");
        return v == null ? undefined : readBoolean(v);
      })(),
    };
  }

  function dropUndefined(obj) {
    const out = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined) {
        out[key] = obj[key];
      }
    }
    return out;
  }

  function resolveConfig() {
    const abCfg = (window && window.ABTestConfig) || {};
    const mvtAliasCfg = (window && window.MVT) || {};
    const mvtCfg = (window && window.MVTConfig) || {};
    const script = typeof document !== "undefined" ? getLoaderScript() : null;
    const attrCfg = script ? resolveFromScriptAttributes(script) : {};

    const base = Object.assign({}, DEFAULTS, dropUndefined(attrCfg));

    const { configApiEndpoint: _ignore1, ...abCfgSansEndpoint } = abCfg;
    const { configApiEndpoint: _ignore2, ...mvtAliasSansEndpoint } = mvtAliasCfg;
    const { configApiEndpoint: _ignore3, ...mvtCfgSansEndpoint } = mvtCfg;
    const merged = Object.assign(
      {},
      base,
      mvtAliasSansEndpoint,
      mvtCfgSansEndpoint,
      abCfgSansEndpoint,
    );

    if (window && (window.MVT || window.MVTConfig) && !merged.projectKeyParam) {
      merged.projectKeyParam = "key";
    }

    merged.configApiEndpoint = __MVT_API_ENDPOINT__;
    merged.changesApiBase = __MVT_CHANGES_API_BASE__;
    merged.visualEditorUrl = __MVT_VISUAL_EDITOR_URL__;
    merged.publicApiBase = __MVT_PUBLIC_API_BASE__;

    return merged;
  }

  window.MVTConfigModule = {
    getConfig: resolveConfig,
  };
})(window, document);
